import dayjs from "dayjs";
import { createContext, useState } from "react";
import { convertBrazilianDateToObject } from "../service/date.service";

const defaultPassenger = {
    fullName: '', 
    cpf: '', 
    birthDate: '', 
    email: '', 
    phone: '',
    gender: ''
}

const defaultBillingInfo = {
    ...defaultPassenger,
    cep: '', 
    address: '', 
    addressNumber: '', 
    neighborhood: '', 
    otherInfo: '', 
    city: '', 
    state: ''
}

const defaultCreditCardInfo = {
    banner: '',
    number: '',
    fullName: '',
    month: '',
    year: '',
    cvv: '',
    installments: ''
}

const defaultPaymentInfo = {
    method: 'credit-card',
    subtotal: 0,
    discount: 0,
    total: 0,
    creditCardInfo: defaultCreditCardInfo
}

const defaultEmergencyContact = {
    fullName: '',
    phone: ''
}

export const CheckoutContext = createContext({
    orderId: null,
    setOrderId: () => {},
    selectedInsurance: {},
    setSelectedInsurance: () => {},
    passengers: [],
    setPassengers: () => {},
    billingInfo: {},
    setBillingInfo: () => {},
    paymentInfo: {},
    setPaymentInfo: () => {},
    emergencyContact: {},
    setEmergencyContact: () => {},
    clearCreditCardInfo: () => {}
});

export const CheckoutProvider = ({children}) => {
    const [orderId, setOrderId] = useState(null);
    const [selectedInsurance, setSelectedInsurance] = useState({});
    const [passengers, setPassengers] = useState([defaultPassenger]);
    const [billingInfo, setBillingInfo] = useState(defaultBillingInfo);
    const [paymentInfo, setPaymentInfo] = useState(defaultPaymentInfo);
    const [youngPassengers, setYoungPassengers] = useState(1);
    const [oldPassengers, setOldPassengers] = useState(0);
    const [emergencyContact, setEmergencyContact] = useState(defaultEmergencyContact);
    
    const addPassenger = () => {
        setPassengers([...passengers, defaultPassenger]);
        setYoungPassengers(youngPassengers + 1);
        recalculateValues();
    }

    const removePassenger = (passenger) => {
        if (passengers.length > 1) {
            let passengerIdx = passengers.indexOf(passenger);
            passengers.splice(passengerIdx, 1);

            setPassengers([...passengers]);
            countPassengersByAge();
        }
    }

    const countPassengersByAge = () => {
        let youngPassCount = 0, oldPassCount = 0;

        passengers.forEach(passenger => {
            const { birthDate } = passenger;

            if (birthDate) {
                const parsedBirthDate = convertBrazilianDateToObject(birthDate);
                const age = dayjs().diff(parsedBirthDate, 'year');

                if (age >= 65) {
                    oldPassCount++;
                } else {
                    youngPassCount++;
                }
            } else {
                youngPassCount++;
            }
        });

        setYoungPassengers(youngPassCount);
        setOldPassengers(oldPassCount);
        recalculateValues();
    }

    const recalculateValues = () => {
        const { passengerPrice, oldPassengerPrice } = selectedInsurance;

        const subtotal = (passengerPrice * youngPassengers) + (oldPassengerPrice * oldPassengers);
        // Só cartão de crédito é aceito; o desconto de PIX (5%) foi removido.
        const discount = 0;
        const total = subtotal - discount;

        setPaymentInfo({
            ...paymentInfo,
            subtotal,
            discount,
            total
        });
    }

    const clearCreditCardInfo = () => {
        setPaymentInfo({
            ...paymentInfo,
            creditCardInfo: defaultCreditCardInfo
        });
    }

    const value = {
        orderId,
        setOrderId,
        selectedInsurance,
        setSelectedInsurance, 
        passengers,
        setPassengers,
        addPassenger,
        removePassenger,
        billingInfo,
        setBillingInfo,
        paymentInfo,
        setPaymentInfo,
        countPassengersByAge,
        youngPassengers,
        oldPassengers,
        emergencyContact, 
        setEmergencyContact,
        clearCreditCardInfo
    };

    return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>;
}